import React, { useState, useEffect } from 'react';
import './Pages.css';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit mode states
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  
  // Profile edit fields
  const [fullname, setFullname] = useState('');
  const [phone, setPhone] = useState('');
  
  // Password change fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const userEmail = localStorage.getItem('userEmail');
    
    if (!userEmail) {
      setError('User email not found');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/profile/${userEmail}`);
      const data = await response.json();

      if (data.success) {
        setProfile(data.profile);
        setFullname(data.profile.Fullname);
        setPhone(data.profile.Phone);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!fullname || !phone) {
      setError('Fullname and phone are required');
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/profile/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: profile.Email,
          fullname,
          phone
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Profile updated successfully');
        setEditingProfile(false);
        
        // Update localStorage username if fullname changed
        localStorage.setItem('username', fullname);
        
        // Refresh profile
        fetchProfile();
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All password fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/profile/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: profile.Email,
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Password changed successfully');
        setEditingPassword(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setError('Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container">
        <div className="error-message">{error || 'Profile not found'}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>👤 My Profile</h1>
          <p>Manage your account information and settings</p>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '20px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '20px' }}>{success}</div>}

      {/* Profile Information Card */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Profile Information</h3>
          {!editingProfile && (
            <button 
              className="primary-btn"
              onClick={() => setEditingProfile(true)}
            >
              ✏️ Edit Profile
            </button>
          )}
        </div>
        <div className="card-body">
          {editingProfile ? (
            <form onSubmit={handleUpdateProfile} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullname">Full Name *</label>
                  <input
                    type="text"
                    id="fullname"
                    value={fullname}
                    onChange={(e) => setFullname(e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="primary-btn">
                  💾 Save Changes
                </button>
                <button 
                  type="button" 
                  className="secondary-btn"
                  onClick={() => {
                    setEditingProfile(false);
                    setFullname(profile.Fullname);
                    setPhone(profile.Phone);
                    setError('');
                  }}
                >
                  ❌ Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <label>Full Name</label>
                <div className="info-value">{profile.Fullname}</div>
              </div>
              <div className="info-item">
                <label>Phone Number</label>
                <div className="info-value">{profile.Phone}</div>
              </div>
              <div className="info-item">
                <label>Email Address</label>
                <div className="info-value readonly">{profile.Email}</div>
              </div>
              <div className="info-item">
                <label>Username</label>
                <div className="info-value readonly">{profile.Username}</div>
              </div>
              <div className="info-item">
                <label>Role</label>
                <div className="info-value readonly">
                  {profile.Role === 1 ? 'Administrator' : 'Policy Manager'}
                </div>
              </div>
              <div className="info-item">
                <label>Member Since</label>
                <div className="info-value readonly">
                  {new Date(profile.DateCreated).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Card */}
      <div className="card">
        <div className="card-header">
          <h3>Security Settings</h3>
          {!editingPassword && (
            <button 
              className="primary-btn"
              onClick={() => setEditingPassword(true)}
            >
              🔒 Change Password
            </button>
          )}
        </div>
        <div className="card-body">
          {editingPassword ? (
            <form onSubmit={handleChangePassword} className="profile-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password *</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="newPassword">New Password *</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="primary-btn">
                  🔑 Update Password
                </button>
                <button 
                  type="button" 
                  className="secondary-btn"
                  onClick={() => {
                    setEditingPassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                >
                  ❌ Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="info-message">
              <p>🔐 Your password is encrypted and secure. Click "Change Password" to update it.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
